/**
 * R232q5b0: Sirus-only race+gender → GLB resolver.
 *
 * Hard rule (USER MANDATE 2026-05-04): NO Quaternius cartoon, NO legacy
 * AIWARI extraction, NO KayKit. Only Sirus M2→glTF GLBs hosted on the
 * AIWARI CDN at:
 *
 *     /aiwari/models/sirus/Character/<Race>/<Gender>/<Race><gender>.glb
 *
 * (capitalised primary path) plus an all-lowercase mirror for case-sensitive
 * file systems. If both fail, the loader builds a neutral procedural
 * placeholder ("Loading Sirus rig...") — NOT a cartoon Quaternius body.
 *
 * Naming caveats (verified by HEAD-probe 2026-05-04 against live VPS):
 *   - Single-word races: folder + filename use first-letter-cap + rest-lower
 *     for both segments. e.g. `Human/Male/Humanmale.glb`,
 *     `Tauren/Female/Taurenfemale.glb`.
 *   - Two-word races (NightElf, BloodElf): folder is camelCase
 *     (`NightElf/Male/`) but the filename only has the leading letter
 *     capital with everything else lowercase: `Nightelfmale.glb`,
 *     `Bloodelffemale.glb`.
 *   - Lowercase mirror is fully lowercase across the board:
 *     `nightelf/male/nightelfmale.glb`.
 *
 * 18/22 race+gender GLBs verified binary 200, 22/22 verified via the
 * lowercase mirror (Sirus extraction script wrote both sides).
 */

import type { Color3 } from '@babylonjs/core';

/** Race ids — 8 WotLK 3.3.5a + 3 outcasts. Matches AIWARI RACES roster. */
export type Race =
  | 'human'
  | 'dwarf'
  | 'night-elf'
  | 'gnome'
  | 'orc'
  | 'undead'
  | 'tauren'
  | 'troll'
  | 'goblin'
  | 'half-blood'
  | 'shadowkin';

export type Gender = 'male' | 'female';

export interface RaceModelConfig {
  /** Sirus-extracted GLB filename (chain-step relative). */
  glb: string;
  /** Target tallest dimension in metres (bbox-normalize in loader). */
  targetHeight: number;
  /** Skin/material tint multiplier. */
  tint?: { r: number; g: number; b: number };
  /** Documentation note. */
  styleNote?: string;
}

/** Base dir for Sirus M2→glTF capitalised character GLBs (chain step 0). */
export const SIRUS_CHAR_BASE = `${import.meta.env.BASE_URL}models/sirus/Character/`;

/** Base dir for Sirus M2→glTF lowercase mirror (chain step 1). */
export const SIRUS_CHAR_BASE_LOWER = `${import.meta.env.BASE_URL}models/sirus/character/`;

/** Legacy export kept so existing imports compile — points at Sirus dir. */
export const AIWARI_CHAR_BASE = SIRUS_CHAR_BASE;

/** Legacy placeholder string kept so existing imports compile — empty value
 *  means "use procedural body inside loader" (no GLB to fetch). */
export const AIWARI_CHAR_PLACEHOLDER = '__procedural__';

/** AIWARI race id → Sirus folder name (Capitalised camelCase per Sirus M2
 *  extractor naming conventions). Sirus has no half-blood/shadowkin races —
 *  those map to closest-visual options (Human / NightElf). */
const SIRUS_RACE_FOLDER: Record<Race, string> = {
  human:        'Human',
  dwarf:        'Dwarf',
  'night-elf':  'NightElf',
  gnome:        'Gnome',
  orc:          'Orc',
  undead:       'Scourge',
  tauren:       'Tauren',
  troll:        'Troll',
  goblin:       'Goblin',
  'half-blood': 'Human',     // outcast — closest visual = Human
  shadowkin:    'NightElf',  // outcast — dark elf → NightElf body
};

/** Sirus race folder → filename basename (everything before gender suffix).
 *  Verified via HEAD-probe vs live VPS:
 *    NightElf/Male/Nightelfmale.glb  (folder camelCase, file leading-cap-only)
 *    Human/Male/Humanmale.glb        (single-word: identical) */
const SIRUS_FILE_BASENAME: Record<string, string> = {
  Human:    'Human',
  Dwarf:    'Dwarf',
  NightElf: 'Nightelf',  // NB: file uses leading-cap, NOT camelCase folder name
  Gnome:    'Gnome',
  Orc:      'Orc',
  Scourge:  'Scourge',
  Tauren:   'Tauren',
  Troll:    'Troll',
  Draenei:  'Draenei',
  BloodElf: 'Bloodelf',  // NB: file uses leading-cap, NOT camelCase folder name
  Goblin:   'Goblin',
};

/** Per-race target heights (metres) — feeds bbox auto-scale in loader. */
const RACE_TARGET_HEIGHT: Record<Race, number> = {
  human:        1.85,
  dwarf:        1.40,
  'night-elf':  2.10,
  gnome:        1.20,
  orc:          2.05,
  undead:       1.85,
  tauren:       2.40,
  troll:        2.20,
  goblin:       1.25,
  'half-blood': 1.85,
  shadowkin:    2.05,
};

/** Per-race optional skin tint (preserved from earlier passes for
 *  consistency with Sirus baked textures — tint is currently *not*
 *  applied at material level since Sirus rigs ship pre-textured, but
 *  we keep the field so nameplate accents and post-FX can tap it). */
const RACE_TINT: Partial<Record<Race, { r: number; g: number; b: number }>> = {
  'night-elf':  { r: 0.78, g: 0.62, b: 0.92 },
  orc:          { r: 0.45, g: 0.75, b: 0.32 },
  undead:       { r: 0.78, g: 0.82, b: 0.72 },
  tauren:       { r: 0.62, g: 0.45, b: 0.32 },
  troll:        { r: 0.45, g: 0.65, b: 0.78 },
  goblin:       { r: 0.55, g: 0.72, b: 0.32 },
  shadowkin:    { r: 0.55, g: 0.45, b: 0.72 },
};

/** Single chain step — caller HEAD-probes baseDir+glb. */
export interface RaceModelChainStep {
  glb: string;
  baseDir: string;
}

export interface RaceModelPick {
  /** First chain entry — preserved for callers that only check primary/placeholder. */
  primary: string;
  /** Last-resort placeholder marker (procedural body inside loader). */
  placeholder: string;
  /** Base dir matching `primary`. */
  baseDir: string;
  /** Full ordered fallback chain. Callers SHOULD iterate this and HEAD-probe
   *  each `${step.baseDir}${step.glb}` until one returns 200. */
  chain: RaceModelChainStep[];
  /** Manual scale multiplier (on top of bbox auto-normalize). */
  scale: number;
  /** Target tallest dim in metres. */
  targetHeight: number;
  /** Optional skin tint. */
  tint?: { r: number; g: number; b: number };
}

/**
 * Resolves the GLB chain for (race, gender).
 *
 * Chain (Sirus only — no Quaternius, no legacy AIWARI):
 *   0. /aiwari/models/sirus/Character/<Race>/<Gender>/<File><gender>.glb   (capitalised primary)
 *   1. /aiwari/models/sirus/character/<race>/<gender>/<file><gender>.glb   (lowercase mirror)
 *   2. __procedural__                                                       (loader builds capsule+sphere)
 */
export function getRaceModelGlb(race: Race, gender: Gender): RaceModelPick {
  const folderCap = SIRUS_RACE_FOLDER[race] ?? 'Human';
  const fileBaseCap = SIRUS_FILE_BASENAME[folderCap] ?? folderCap;
  const folderLow = folderCap.toLowerCase();
  const fileBaseLow = fileBaseCap.toLowerCase();
  const genderCap = gender === 'female' ? 'Female' : 'Male';
  const genderLow = gender === 'female' ? 'female' : 'male';

  // R232q86zg: REVERT WotLK 3.3.5a. User mandate: ONLY Sirus 1:1. Sirus v2
  // GLB (re-extracted from MPQ via m2_to_glb_v2.py + skeleton_flatten.py) is
  // 7.17MB, has 138 bones, 156 animations, 11037 channels — full WoW
  // character with skeleton + per-anim rotation/translation/scale tracks.
  // Step 0: capitalised Sirus primary (e.g. Character/NightElf/Male/Nightelfmale.glb).
  const sirusCapStep: RaceModelChainStep = {
    glb: `${fileBaseCap}${genderLow}.glb`,
    baseDir: `${SIRUS_CHAR_BASE}${folderCap}/${genderCap}/`,
  };

  // Step 1: lowercase Sirus mirror (e.g. character/nightelf/male/nightelfmale.glb).
  const sirusLowStep: RaceModelChainStep = {
    glb: `${fileBaseLow}${genderLow}.glb`,
    baseDir: `${SIRUS_CHAR_BASE_LOWER}${folderLow}/${genderLow}/`,
  };

  // Step 2: procedural placeholder marker — loader detects empty GLB name
  // (or HEAD-probe failure on real path) and builds a procedural body.
  const placeholderStep: RaceModelChainStep = {
    glb: AIWARI_CHAR_PLACEHOLDER,
    baseDir: SIRUS_CHAR_BASE,
  };

  const chain = [sirusCapStep, sirusLowStep, placeholderStep];

  return {
    primary:      chain[0].glb,
    placeholder:  AIWARI_CHAR_PLACEHOLDER,
    baseDir:      chain[0].baseDir,
    chain,
    scale:        1.0,
    targetHeight: RACE_TARGET_HEIGHT[race] ?? 1.85,
    tint:         RACE_TINT[race],
  };
}

/**
 * Returns ordered chain for female loading.
 * Order matches `getRaceModelGlb(race, 'female').chain`.
 */
export function getFemaleModelChain(race: Race): RaceModelChainStep[] {
  return getRaceModelGlb(race, 'female').chain;
}

/** Color3 helper — stub kept for compat. */
export function applyRaceTint(_color3: Color3, _race: Race, _gender: Gender): void {
  // intentionally empty — Sirus rigs ship baked textures.
}
